import React, { useState, useRef, useEffect } from 'react';
import { FaMountain, FaMapMarkerAlt, FaClock, FaArrowUp, FaArrowDown, FaRuler, FaChartLine, FaInfoCircle, FaPlus, FaTrash, FaRoute, FaExclamationTriangle, FaCheck, FaWalking, FaSearch, FaKeyboard, FaFire, FaHiking, FaShoePrints, FaCloudSun, FaSuitcase, FaExclamationCircle } from 'react-icons/fa';
import { analyzeTrailDifficulty, analyzeTrailFromCoords, getElevationProfile, searchTrailByName, analyzeTrailByName } from '../../api/client';

const TrailDifficulty = () => {
  // Search mode: 'name' or 'coordinates'
  const [searchMode, setSearchMode] = useState('name');

  // Trail name search fields
  const [trailName, setTrailName] = useState('');
  const [place, setPlace] = useState('');
  const [state, setState] = useState('');

  // Search results for trail selection
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTrail, setSelectedTrail] = useState(null);
  const [searching, setSearching] = useState(false);

  // Coordinate-based waypoints
  const [waypoints, setWaypoints] = useState([
    { lat: '', lng: '', name: 'Start Point' },
    { lat: '', lng: '', name: 'End Point' }
  ]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);

  // Difficulty colors
  const difficultyColors = {
    'Easy': { bg: 'bg-green-500', text: 'text-green-700', light: 'bg-green-50' },
    'Moderate': { bg: 'bg-yellow-500', text: 'text-yellow-700', light: 'bg-yellow-50' },
    'Difficult': { bg: 'bg-orange-500', text: 'text-orange-700', light: 'bg-orange-50' },
    'Strenuous': { bg: 'bg-red-500', text: 'text-red-700', light: 'bg-red-50' },
    'Expert': { bg: 'bg-purple-500', text: 'text-purple-700', light: 'bg-purple-50' }
  };

  // Add waypoint
  const addWaypoint = () => {
    const newWaypoints = [...waypoints];
    newWaypoints.splice(waypoints.length - 1, 0, {
      lat: '',
      lng: '',
      name: `Via Point ${waypoints.length - 1}`
    });
    setWaypoints(newWaypoints);
  };

  // Remove waypoint
  const removeWaypoint = (index) => {
    if (waypoints.length <= 2) return;
    const newWaypoints = waypoints.filter((_, i) => i !== index);
    setWaypoints(newWaypoints);
  };

  // Update waypoint
  const updateWaypoint = (index, field, value) => {
    const newWaypoints = [...waypoints];
    newWaypoints[index][field] = value;
    setWaypoints(newWaypoints);
  };

  // Use current location for a waypoint
  const setLocationFromGPS = (index) => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newWaypoints = [...waypoints];
          newWaypoints[index].lat = position.coords.latitude.toFixed(6);
          newWaypoints[index].lng = position.coords.longitude.toFixed(6);
          setWaypoints(newWaypoints);
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Could not get your location.');
        }
      );
    }
  };

  // Search for trails by name
  const searchForTrails = async () => {
    if (!trailName.trim() || !place.trim()) {
      setError('Please enter both trail name and place');
      return;
    }

    setSearching(true);
    setError(null);
    setSearchResults([]);
    setSelectedTrail(null);
    setResult(null);

    try {
      const data = await searchTrailByName(
        trailName.trim(),
        place.trim(),
        state.trim() || null,
        'USA'
      );

      if (data.results && data.results.length > 0) {
        setSearchResults(data.results);
        // Auto-select if only one result
        if (data.results.length === 1) {
          setSelectedTrail(data.results[0]);
        }
      } else {
        setError('No trails found. Try different search terms or use coordinates mode.');
      }
    } catch (err) {
      console.error('Error searching for trail:', err);
      setError(err.response?.data?.detail || 'Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  // Analyze selected trail
  const analyzeSelectedTrail = async () => {
    if (!selectedTrail) {
      setError('Please select a trail from the search results');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Use the coordinates from selected trail
      const waypoints = [
        { lat: selectedTrail.lat, lng: selectedTrail.lng }
      ];

      // Create a simple out-and-back path for analysis
      const latOffset = 0.045; // ~5km
      const lngOffset = 0.045 / Math.cos(selectedTrail.lat * Math.PI / 180);

      const analysisWaypoints = [
        { lat: selectedTrail.lat, lng: selectedTrail.lng },
        { lat: selectedTrail.lat + latOffset * 0.5, lng: selectedTrail.lng + lngOffset * 0.25 },
        { lat: selectedTrail.lat + latOffset, lng: selectedTrail.lng },
      ];

      const data = await analyzeTrailDifficulty(analysisWaypoints);

      // Add trail info to result
      setResult({
        ...data,
        trail_info: {
          name: selectedTrail.trail_name,
          location: selectedTrail.location,
          search_lat: selectedTrail.lat,
          search_lng: selectedTrail.lng,
          display_name: selectedTrail.display_name,
          note: "This analysis is an estimate based on the trail's general location. For precise results, use GPS coordinates from the actual trail."
        }
      });
    } catch (err) {
      console.error('Error analyzing trail:', err);
      setError(err.response?.data?.detail || 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Clear search and start over
  const clearSearch = () => {
    setSearchResults([]);
    setSelectedTrail(null);
    setResult(null);
    setError(null);
  };

  // Analyze trail by coordinates
  const analyzeTrail = async () => {
    // Validate waypoints
    const validWaypoints = waypoints.filter(w => w.lat && w.lng);
    if (validWaypoints.length < 2) {
      setError('Please enter at least 2 valid coordinates');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await analyzeTrailDifficulty(
        validWaypoints.map(w => ({
          lat: parseFloat(w.lat),
          lng: parseFloat(w.lng)
        }))
      );
      setResult(data);
    } catch (err) {
      console.error('Error analyzing trail:', err);
      setError(err.response?.data?.detail || 'Failed to analyze trail. Please check your coordinates.');
    } finally {
      setLoading(false);
    }
  };

  // Draw elevation profile on canvas
  useEffect(() => {
    if (!result || !result.profile || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const profile = result.profile;

    // Set canvas size
    const width = canvas.offsetWidth;
    const height = 200;
    canvas.width = width * 2; // Retina
    canvas.height = height * 2;
    ctx.scale(2, 2);

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Find min/max elevations
    const elevations = profile.map(p => p.elevation_m);
    const minElev = Math.min(...elevations);
    const maxElev = Math.max(...elevations);
    const elevRange = maxElev - minElev || 1;

    // Calculate distances
    const maxDist = profile[profile.length - 1]?.distance_km || 1;

    // Draw background grid
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 0.5;
    for (let i = 0; i <= 4; i++) {
      const y = (i / 4) * (height - 40) + 20;
      ctx.beginPath();
      ctx.moveTo(50, y);
      ctx.lineTo(width - 20, y);
      ctx.stroke();
    }

    // Draw elevation profile
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, 'rgba(251, 146, 60, 0.8)');
    gradient.addColorStop(1, 'rgba(251, 146, 60, 0.1)');

    ctx.beginPath();
    ctx.moveTo(50, height - 20);

    profile.forEach((point, i) => {
      const x = 50 + ((point.distance_km / maxDist) * (width - 70));
      const y = height - 20 - ((point.elevation_m - minElev) / elevRange * (height - 50));
      if (i === 0) {
        ctx.lineTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    ctx.lineTo(50 + ((profile[profile.length - 1].distance_km / maxDist) * (width - 70)), height - 20);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();

    // Draw line on top
    ctx.beginPath();
    profile.forEach((point, i) => {
      const x = 50 + ((point.distance_km / maxDist) * (width - 70));
      const y = height - 20 - ((point.elevation_m - minElev) / elevRange * (height - 50));
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.strokeStyle = '#f97316';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw axes labels
    ctx.fillStyle = '#6b7280';
    ctx.font = '11px system-ui';
    ctx.textAlign = 'right';

    // Y-axis (elevation)
    for (let i = 0; i <= 4; i++) {
      const elev = minElev + (elevRange * (4 - i) / 4);
      const y = (i / 4) * (height - 40) + 24;
      ctx.fillText(`${Math.round(elev)}m`, 45, y);
    }

    // X-axis (distance)
    ctx.textAlign = 'center';
    for (let i = 0; i <= 4; i++) {
      const dist = (maxDist * i / 4).toFixed(1);
      const x = 50 + ((i / 4) * (width - 70));
      ctx.fillText(`${dist}km`, x, height - 5);
    }

  }, [result]);

  // Load sample trail (Grand Canyon example)
  const loadSampleTrail = () => {
    setWaypoints([
      { lat: '36.0594', lng: '-112.1401', name: 'South Kaibab Trailhead' },
      { lat: '36.0991', lng: '-112.0955', name: 'Phantom Ranch' }
    ]);
  };

  return (
    <div className="space-y-6">
      {/* Hero Card */}
      <div className="bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 rounded-3xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          {/* Mountain silhouette */}
          <svg className="absolute bottom-0 left-0 right-0 h-32 text-white/10" viewBox="0 0 400 100" preserveAspectRatio="none">
            <polygon points="0,100 50,40 100,70 150,30 200,60 250,20 300,50 350,35 400,80 400,100" fill="currentColor" />
          </svg>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <FaMountain className="text-2xl text-yellow-200" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Trail Difficulty Auditor</h2>
              <p className="text-white/70">Analyze any hiking trail</p>
            </div>
          </div>

          <p className="text-white/90 max-w-2xl">
            Enter GPS coordinates for your trail to get elevation profiles, difficulty ratings,
            and time estimates based on the Shenandoah hiking formula.
          </p>
        </div>
      </div>

      {/* Search Mode Toggle */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-neutral-100">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-neutral-800 flex items-center gap-2">
            <FaRoute className="text-orange-500" />
            Find Your Trail
          </h3>
          <div className="flex bg-neutral-100 rounded-xl p-1">
            <button
              onClick={() => setSearchMode('name')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                searchMode === 'name'
                  ? 'bg-white text-orange-600 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-800'
              }`}
            >
              <FaSearch className="text-sm" />
              Search by Name
            </button>
            <button
              onClick={() => setSearchMode('coordinates')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                searchMode === 'coordinates'
                  ? 'bg-white text-orange-600 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-800'
              }`}
            >
              <FaKeyboard className="text-sm" />
              Enter Coordinates
            </button>
          </div>
        </div>

        {/* Trail Name Search Mode */}
        {searchMode === 'name' && (
          <div className="space-y-4">
            {/* Search Form */}
            {searchResults.length === 0 && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Trail Name *
                    </label>
                    <input
                      type="text"
                      value={trailName}
                      onChange={(e) => setTrailName(e.target.value)}
                      className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="e.g., Half Dome Trail"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Place / Park *
                    </label>
                    <input
                      type="text"
                      value={place}
                      onChange={(e) => setPlace(e.target.value)}
                      className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="e.g., Yosemite National Park"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      State (optional)
                    </label>
                    <input
                      type="text"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="e.g., California"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4 pt-4">
                  <button
                    onClick={searchForTrails}
                    disabled={searching || !trailName.trim() || !place.trim()}
                    className="flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-bold transition-all disabled:opacity-50"
                  >
                    {searching ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <FaSearch />
                        Search Trail
                      </>
                    )}
                  </button>
                </div>

                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <FaInfoCircle className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-800">
                      <strong>Tip:</strong> For best results, enter the trail name exactly as known (e.g., "Angel's Landing", "Half Dome Trail").
                      The analysis estimates difficulty based on the trail's general terrain. For precise results, use the coordinates mode with actual GPS waypoints.
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Search Results - Select Trail */}
            {searchResults.length > 0 && !result && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-neutral-800">
                    {searchResults.length === 1 ? '1 Result Found' : `${searchResults.length} Results Found`} - Select a Trail
                  </h4>
                  <button
                    onClick={clearSearch}
                    className="text-sm text-neutral-500 hover:text-neutral-700"
                  >
                    New Search
                  </button>
                </div>

                <div className="space-y-2">
                  {searchResults.map((trail, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedTrail(trail)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedTrail === trail
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-neutral-200 bg-white hover:border-orange-300 hover:bg-orange-50/50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          selectedTrail === trail ? 'bg-orange-500 text-white' : 'bg-neutral-200 text-neutral-500'
                        }`}>
                          {selectedTrail === trail ? <FaCheck className="text-xs" /> : (index + 1)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-neutral-800">{trail.display_name}</div>
                          <div className="text-sm text-neutral-500 mt-1">
                            <FaMapMarkerAlt className="inline mr-1" />
                            {trail.lat.toFixed(4)}, {trail.lng.toFixed(4)}
                            {trail.osm_type && <span className="ml-2 px-2 py-0.5 bg-neutral-100 rounded text-xs">{trail.osm_type}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-4 pt-4">
                  <button
                    onClick={analyzeSelectedTrail}
                    disabled={loading || !selectedTrail}
                    className="flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-bold transition-all disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <FaChartLine />
                        Analyze Selected Trail
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Coordinates Mode */}
        {searchMode === 'coordinates' && (
          <div className="space-y-4">
            <div className="flex justify-end mb-2">
              <button
                onClick={loadSampleTrail}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Load Sample Trail
              </button>
            </div>

            {waypoints.map((waypoint, index) => (
              <div key={index} className="flex items-center gap-4 p-4 bg-neutral-50 rounded-xl">
                <div className="flex-shrink-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                    index === 0 ? 'bg-green-500' :
                    index === waypoints.length - 1 ? 'bg-red-500' : 'bg-orange-500'
                  }`}>
                    {index === 0 ? 'S' : index === waypoints.length - 1 ? 'E' : index}
                  </div>
                </div>

                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1">
                      Name (optional)
                    </label>
                    <input
                      type="text"
                      value={waypoint.name}
                      onChange={(e) => updateWaypoint(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Point name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1">
                      Latitude
                    </label>
                    <input
                      type="text"
                      value={waypoint.lat}
                      onChange={(e) => updateWaypoint(index, 'lat', e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="e.g. 36.0594"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1">
                      Longitude
                    </label>
                    <input
                      type="text"
                      value={waypoint.lng}
                      onChange={(e) => updateWaypoint(index, 'lng', e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="e.g. -112.1401"
                    />
                  </div>
                </div>

                <div className="flex-shrink-0 flex items-center gap-2">
                  <button
                    onClick={() => setLocationFromGPS(index)}
                    className="p-2 text-neutral-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    title="Use my location"
                  >
                    <FaMapMarkerAlt />
                  </button>
                  {waypoints.length > 2 && index > 0 && index < waypoints.length - 1 && (
                    <button
                      onClick={() => removeWaypoint(index)}
                      className="p-2 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove waypoint"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              </div>
            ))}

            <div className="flex items-center gap-4 pt-4">
              <button
                onClick={addWaypoint}
                className="flex items-center gap-2 px-4 py-2 text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-xl font-medium transition-colors"
              >
                <FaPlus />
                Add Via Point
              </button>

              <button
                onClick={analyzeTrail}
                disabled={loading}
                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-bold transition-all disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <FaChartLine />
                    Analyze Trail
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
            <FaExclamationTriangle />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-6 animate-fade-in">
          {/* New Search Button */}
          <div className="flex justify-end">
            <button
              onClick={clearSearch}
              className="flex items-center gap-2 px-4 py-2 text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-xl font-medium transition-colors"
            >
              <FaSearch />
              Search New Trail
            </button>
          </div>

          {/* Trail Info (when searched by name) */}
          {result.trail_info && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-green-100 rounded-xl">
                  <FaMapMarkerAlt className="text-xl text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-green-900">{result.trail_info.name}</h3>
                  <p className="text-green-700 mt-1">{result.trail_info.location}</p>
                  <p className="text-sm text-green-600 mt-2">{result.trail_info.display_name}</p>
                  {result.trail_info.note && (
                    <div className="mt-3 p-3 bg-white/60 rounded-lg">
                      <p className="text-sm text-green-800">
                        <FaInfoCircle className="inline mr-2" />
                        {result.trail_info.note}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Difficulty Rating Card */}
          <div className={`rounded-2xl p-8 ${difficultyColors[result.difficulty_rating]?.light || 'bg-neutral-50'}`}>
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className={`w-24 h-24 rounded-2xl ${difficultyColors[result.difficulty_rating]?.bg || 'bg-neutral-500'} flex items-center justify-center text-white`}>
                <div className="text-center">
                  <div className="text-3xl font-bold">{result.difficulty_score}</div>
                  <div className="text-xs opacity-80">/10</div>
                </div>
              </div>
              <div className="text-center md:text-left">
                <h3 className={`text-3xl font-bold ${difficultyColors[result.difficulty_rating]?.text || 'text-neutral-700'}`}>
                  {result.difficulty_rating}
                </h3>
                <p className="text-neutral-600 mt-1">Trail Difficulty Rating</p>
              </div>
              <div className="ml-auto hidden md:flex items-center gap-2">
                <FaWalking className={`text-2xl ${difficultyColors[result.difficulty_rating]?.text || 'text-neutral-500'}`} />
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl shadow-sm p-5 border border-neutral-100">
              <div className="flex items-center gap-2 text-neutral-500 mb-2">
                <FaRuler className="text-orange-500" />
                <span className="text-sm font-medium">Distance</span>
              </div>
              <div className="text-2xl font-bold text-neutral-800">
                {result.total_distance_km} km
              </div>
              <div className="text-sm text-neutral-500">
                {(result.total_distance_km * 0.621).toFixed(1)} miles
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-5 border border-neutral-100">
              <div className="flex items-center gap-2 text-neutral-500 mb-2">
                <FaArrowUp className="text-green-500" />
                <span className="text-sm font-medium">Elevation Gain</span>
              </div>
              <div className="text-2xl font-bold text-neutral-800">
                {result.total_elevation_gain_m} m
              </div>
              <div className="text-sm text-neutral-500">
                {Math.round(result.total_elevation_gain_m * 3.281)} ft
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-5 border border-neutral-100">
              <div className="flex items-center gap-2 text-neutral-500 mb-2">
                <FaArrowDown className="text-red-500" />
                <span className="text-sm font-medium">Elevation Loss</span>
              </div>
              <div className="text-2xl font-bold text-neutral-800">
                {result.total_elevation_loss_m} m
              </div>
              <div className="text-sm text-neutral-500">
                {Math.round(result.total_elevation_loss_m * 3.281)} ft
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-5 border border-neutral-100">
              <div className="flex items-center gap-2 text-neutral-500 mb-2">
                <FaClock className="text-blue-500" />
                <span className="text-sm font-medium">Est. Time</span>
              </div>
              <div className="text-2xl font-bold text-neutral-800">
                {Math.floor(result.estimated_time_hours)}h {Math.round((result.estimated_time_hours % 1) * 60)}m
              </div>
              <div className="text-sm text-neutral-500">
                Naismith's Rule
              </div>
            </div>
          </div>

          {/* Elevation Profile */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-neutral-100">
            <h3 className="text-lg font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <FaChartLine className="text-orange-500" />
              Elevation Profile
            </h3>
            <div className="relative">
              <canvas
                ref={canvasRef}
                className="w-full"
                style={{ height: '200px' }}
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-4 text-sm text-neutral-600">
              <div>
                <span className="font-medium">Max Elevation:</span> {result.max_elevation_m}m ({Math.round(result.max_elevation_m * 3.281)}ft)
              </div>
              <div>
                <span className="font-medium">Min Elevation:</span> {result.min_elevation_m}m ({Math.round(result.min_elevation_m * 3.281)}ft)
              </div>
              <div>
                <span className="font-medium">Avg Gradient:</span> {result.avg_gradient_percent}%
              </div>
              <div>
                <span className="font-medium">Max Gradient:</span> {result.max_gradient_percent}%
              </div>
            </div>
          </div>

          {/* Terrain & Fitness Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Terrain Breakdown */}
            {result.terrain_breakdown && (
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-neutral-100">
                <h3 className="text-lg font-bold text-neutral-800 mb-4 flex items-center gap-2">
                  <FaMountain className="text-orange-500" />
                  Terrain Breakdown
                </h3>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-neutral-600">Flat (&lt;5%)</span>
                      <span className="font-medium text-green-600">{result.terrain_breakdown.flat_percent}%</span>
                    </div>
                    <div className="h-3 bg-neutral-100 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: `${result.terrain_breakdown.flat_percent}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-neutral-600">Moderate (5-15%)</span>
                      <span className="font-medium text-yellow-600">{result.terrain_breakdown.moderate_percent}%</span>
                    </div>
                    <div className="h-3 bg-neutral-100 rounded-full overflow-hidden">
                      <div className="h-full bg-yellow-500 rounded-full" style={{ width: `${result.terrain_breakdown.moderate_percent}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-neutral-600">Steep (15-25%)</span>
                      <span className="font-medium text-orange-600">{result.terrain_breakdown.steep_percent}%</span>
                    </div>
                    <div className="h-3 bg-neutral-100 rounded-full overflow-hidden">
                      <div className="h-full bg-orange-500 rounded-full" style={{ width: `${result.terrain_breakdown.steep_percent}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-neutral-600">Very Steep (&gt;25%)</span>
                      <span className="font-medium text-red-600">{result.terrain_breakdown.very_steep_percent}%</span>
                    </div>
                    <div className="h-3 bg-neutral-100 rounded-full overflow-hidden">
                      <div className="h-full bg-red-500 rounded-full" style={{ width: `${result.terrain_breakdown.very_steep_percent}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Fitness & Effort */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-800 mb-4 flex items-center gap-2">
                <FaHiking className="text-orange-500" />
                Fitness & Effort
              </h3>
              <div className="space-y-4">
                {result.fitness_level && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FaWalking className="text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-neutral-500">Fitness Level Required</div>
                      <div className="text-neutral-800">{result.fitness_level}</div>
                    </div>
                  </div>
                )}
                {result.calories_estimate && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <FaFire className="text-red-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-neutral-500">Estimated Calories Burned</div>
                      <div className="text-neutral-800 text-xl font-bold">{result.calories_estimate} kcal</div>
                      <div className="text-xs text-neutral-400">Based on 70kg (154lb) hiker</div>
                    </div>
                  </div>
                )}
                {result.pace_recommendation && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <FaShoePrints className="text-green-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-neutral-500">Recommended Pace</div>
                      <div className="text-neutral-800">{result.pace_recommendation}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Technical & Conditions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Steepest Section */}
            {result.steepest_section && (
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border border-red-200">
                <h3 className="text-lg font-bold text-red-900 mb-4 flex items-center gap-2">
                  <FaExclamationCircle className="text-red-500" />
                  Steepest Section
                </h3>
                <div className="bg-white/70 rounded-xl p-4">
                  <div className="text-2xl font-bold text-red-700 mb-2">{result.steepest_section.gradient_percent}% grade</div>
                  <p className="text-neutral-700">{result.steepest_section.description}</p>
                  <div className="mt-2 text-sm text-neutral-500">
                    At km {result.steepest_section.start_km} - {result.steepest_section.end_km}
                  </div>
                </div>
                {result.technical_difficulty && (
                  <div className="mt-4 px-4 py-2 bg-white/50 rounded-lg">
                    <span className="text-sm font-medium text-neutral-600">Technical Rating: </span>
                    <span className="text-neutral-800">{result.technical_difficulty}</span>
                  </div>
                )}
              </div>
            )}

            {/* Best Conditions */}
            {result.best_conditions && (
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200">
                <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
                  <FaCloudSun className="text-blue-500" />
                  Best Conditions
                </h3>
                <p className="text-neutral-700">{result.best_conditions}</p>
              </div>
            )}
          </div>

          {/* Gear Suggestions */}
          {result.gear_suggestions && result.gear_suggestions.length > 0 && (
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-6 border border-amber-200">
              <h3 className="text-lg font-bold text-amber-900 mb-4 flex items-center gap-2">
                <FaSuitcase className="text-amber-600" />
                Recommended Gear
              </h3>
              <div className="flex flex-wrap gap-2">
                {result.gear_suggestions.map((gear, idx) => (
                  <span key={idx} className="px-3 py-1.5 bg-white/70 rounded-full text-sm text-amber-800 border border-amber-200">
                    {gear}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          <div className="bg-orange-50 rounded-2xl p-6 border border-orange-100">
            <h3 className="text-lg font-bold text-orange-900 mb-4 flex items-center gap-2">
              <FaInfoCircle className="text-orange-500" />
              Trail Recommendations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {result.recommendations.map((rec, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-white/60 rounded-xl p-4">
                  <FaCheck className="text-orange-500 mt-1 flex-shrink-0" />
                  <span className="text-neutral-700">{rec}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Info Footer */}
      <div className="bg-neutral-100 rounded-2xl p-6 flex items-start gap-4">
        <FaInfoCircle className="text-neutral-500 text-xl flex-shrink-0 mt-1" />
        <div>
          <h4 className="font-semibold text-neutral-800 mb-1">How It Works</h4>
          <p className="text-sm text-neutral-600">
            This tool uses the <strong>Shenandoah Hiking Difficulty Formula</strong> to calculate trail difficulty:
            sqrt(2 × elevation gain in feet × distance in miles). Time estimates use{' '}
            <strong>Naismith's Rule</strong>: 5km/hour + 1 hour per 600m ascent. Elevation data is sourced
            from SRTM (Shuttle Radar Topography Mission) via OpenTopoData.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrailDifficulty;
